import React from "react";

const sections = [
  {
    id: "about",
    title: "1. About PSBS",
    body: "PSBS provides public transport reservation services for daily commuters. Using the PSBS Android application, passengers can reserve seats, purchase daily reservations, subscribe to recurring transport services, and manage their bookings conveniently.",
    list: [
      "Create and manage an account",
      "Reserve seats on designated routes",
      "Purchase daily reservations",
      "Manage subscription-based travel",
      "View schedules and reservation history",
      "Receive booking notifications",
    ],
  },
  {
    id: "collect",
    title: "2. Information We Collect",
    subsections: [
      {
        title: "Personal Information",
        list: [
          "Full Name",
          "Mobile Number",
          "Email Address",
          "Home, pickup and drop-off locations (where provided)",
        ],
      },
      {
        title: "Reservation Information",
        list: [
          "Selected routes",
          "Boarding and drop-off stops",
          "Reservation dates",
          "Subscription details",
          "Seat assignments",
          "Reservation history",
          "Payment status",
        ],
      },
      {
        title: "Payment Information",
        body: "We collect payment confirmations and transaction reference numbers required to process reservations. PSBS does not store debit or credit card information.",
      },
      {
        title: "Device Information",
        list: [
          "Device model",
          "Android version",
          "Application version",
          "Device identifiers",
          "IP address",
          "Crash reports",
          "Diagnostic information",
        ],
      },
      {
        title: "Location Information",
        body: "With your permission, PSBS may access your device location to improve route selection, identify nearby pickup points and enhance service quality.",
      },
    ],
  },
  {
    id: "use",
    title: "3. How We Use Your Information",
    list: [
      "Create and manage your account",
      "Process reservations",
      "Manage subscriptions",
      "Allocate seats",
      "Process payments",
      "Send confirmations and reminders",
      "Respond to support requests",
      "Improve application performance",
      "Detect fraud",
      "Comply with legal obligations",
    ],
  },
  {
    id: "sharing",
    title: "4. Data Sharing",
    body: "We do not sell or rent your personal information. We may share information only with authorized payment providers, cloud hosting providers, notification services, analytics providers and government authorities where required by law.",
  },
  {
    id: "security",
    title: "5. Data Security",
    body: "We use reasonable administrative, technical and organizational safeguards to protect your information.",
  },
  {
    id: "rights",
    title: "6. Your Rights",
    list: [
      "Access your information",
      "Update your information",
      "Request corrections",
      "Request account deletion",
      "Withdraw permissions where applicable",
    ],
  },
  {
    id: "children",
    title: "7. Children's Privacy",
    body: "PSBS services are not intended for children under 13 years of age.",
  },
  {
    id: "changes",
    title: "8. Changes to this Policy",
    body: "We may update this Privacy Policy from time to time. Updates will be published on our website and within the PSBS application.",
  },
  {
    id: "contact",
    title: "9. Contact Us",
    body: "Website: https://psbs.com.pk\nMobile: +92 329 5630791",
  },
];

const List = ({ items }) => (
  <ul className="list-disc pl-6 mt-3 space-y-2 text-slate-600">
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-100">
      <section className="bg-gradient-to-r from-red-700 to-red-500 text-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <span className="inline-block rounded-full bg-white/20 px-4 py-1 text-sm">
            PSBS Transport Services
          </span>
          <h1 className="mt-6 text-5xl font-bold">Privacy Policy</h1>
          <p className="mt-4 max-w-3xl text-blue-100 text-lg">
            Learn how PSBS collects, uses and protects your information while
            providing bus reservation services.
          </p>
          <p className="mt-6 text-blue-100">Effective Date: January 01, 2026</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-4 gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-8 bg-white rounded-xl shadow p-5">
            <h2 className="font-semibold mb-4">Contents</h2>
            <ul className="space-y-2 text-sm">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-red-600 hover:underline">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <main className="lg:col-span-3">
          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-12">
            {sections.map((sec) => (
              <section id={sec.id} key={sec.id}>
                <h2 className="text-3xl font-bold text-slate-900">
                  {sec.title}
                </h2>
                {sec.body &&
                  sec.body.split("\n").map((p, i) => (
                    <p
                      key={i}
                      className="mt-4 leading-8 text-slate-600 whitespace-pre-line"
                    >
                      {p}
                    </p>
                  ))}
                {sec.list && <List items={sec.list} />}
                {sec.subsections &&
                  sec.subsections.map((sub) => (
                    <div key={sub.title} className="mt-8">
                      <h3 className="text-xl font-semibold text-slate-800">
                        {sub.title}
                      </h3>
                      {sub.body && (
                        <p className="mt-3 leading-8 text-slate-600">
                          {sub.body}
                        </p>
                      )}
                      {sub.list && <List items={sub.list} />}
                    </div>
                  ))}
              </section>
            ))}
            <div className="border-t pt-8">
              <h2 className="text-3xl font-bold">Consent</h2>
              <p className="mt-4 text-slate-600 leading-8">
                By downloading, registering for, or using the PSBS Android
                application or our website, you acknowledge that you have read,
                understood and agreed to this Privacy Policy.
              </p>
            </div>
          </div>
          <footer className="text-center text-slate-500 py-8">
            © 2026 PSBS. All Rights Reserved.
          </footer>
        </main>
      </div>
    </div>
  );
}
